// Life & Wealth Solutions — Call Monitoring Center V34
// Phoenix Rescue Apps — Live call monitoring, whisper mode, recording archive
// Requires Twilio when live — full UI functional now

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, PhoneOff, Volume2, VolumeX, Mic, MicOff,
  Play, Pause, SkipForward, Star, AlertCircle, Users,
  Clock, TrendingUp, Flag, BookOpen, CheckCircle
} from 'lucide-react';

interface ActiveCall {
  id: string;
  agentName: string;
  agentType: 'Human' | 'AI';
  agentId: number;
  customerName: string;
  customerPhone: string;
  policyType: string;
  duration: string;
  durationSecs: number;
  status: 'active' | 'hold';
  flagged: boolean;
}

interface Recording {
  id: string;
  agentName: string;
  agentType: 'Human' | 'AI';
  customerName: string;
  date: string;
  duration: string;
  policyType: string;
  qualityScore: number;
  outcome: 'Policy Sold' | 'Follow-up' | 'No Sale' | 'Transfer';
  notes: string;
  isTraining: boolean;
}

interface CallNote {
  callId: string;
  text: string;
  timestamp: string;
}

const MOCK_CALLS: ActiveCall[] = [
  { id: 'call-001', agentName: 'Sarah', agentType: 'AI', agentId: 1, customerName: 'John Rivera', customerPhone: '(404) 555-0101', policyType: 'Life Insurance', duration: '03:42', durationSecs: 222, status: 'active', flagged: false },
  { id: 'call-002', agentName: 'Mike', agentType: 'AI', agentId: 2, customerName: 'Southeast Catering', customerPhone: '(770) 555-0204', policyType: 'Commercial — Whale', duration: '01:15', durationSecs: 75, status: 'active', flagged: false },
  { id: 'call-003', agentName: 'Emma', agentType: 'AI', agentId: 3, customerName: 'Lisa Thompson', customerPhone: '(678) 555-0312', policyType: 'Home Insurance', duration: '05:30', durationSecs: 330, status: 'active', flagged: false },
  { id: 'call-004', agentName: 'Shawn', agentType: 'Human', agentId: 0, customerName: 'David Park', customerPhone: '(404) 555-0445', policyType: 'Auto Insurance', duration: '00:48', durationSecs: 48, status: 'active', flagged: false },
  { id: 'call-005', agentName: 'Tom', agentType: 'AI', agentId: 4, customerName: 'Marcus Williams', customerPhone: '(770) 555-0567', policyType: 'Commercial Auto', duration: '02:20', durationSecs: 140, status: 'hold', flagged: false },
];

const MOCK_RECORDINGS: Recording[] = [
  { id: 'rec-001', agentName: 'Sarah', agentType: 'AI', customerName: 'Rivera Family', date: '2026-04-13', duration: '08:23', policyType: 'Life Insurance', qualityScore: 4.5, outcome: 'Policy Sold', notes: 'Excellent rapport. Strong close on whole life.', isTraining: true },
  { id: 'rec-002', agentName: 'Emma', agentType: 'AI', customerName: 'Johnson Family', date: '2026-04-13', duration: '04:12', policyType: 'Home Insurance', qualityScore: 4.0, outcome: 'Follow-up', notes: 'Good info delivery. Needs follow-up on deductible question.', isTraining: false },
  { id: 'rec-003', agentName: 'Shawn', agentType: 'Human', customerName: 'ABC Logistics', date: '2026-04-13', duration: '12:45', policyType: 'Commercial Auto', qualityScore: 5.0, outcome: 'Policy Sold', notes: 'Outstanding close technique — whale converted.', isTraining: true },
  { id: 'rec-004', agentName: 'Mike', agentType: 'AI', customerName: 'David Park', date: '2026-04-12', duration: '06:08', policyType: 'Auto Insurance', qualityScore: 3.5, outcome: 'No Sale', notes: 'Objection handling needs work. Price sensitivity.', isTraining: false },
  { id: 'rec-005', agentName: 'Charlotte', agentType: 'AI', customerName: 'Jennifer Adams', date: '2026-04-12', duration: '09:33', policyType: 'Life Insurance', qualityScore: 4.8, outcome: 'Policy Sold', notes: 'Perfect retention conversation. Upsold to whole life.', isTraining: true },
];

const outcomeColor: Record<string, string> = {
  'Policy Sold': 'bg-green-100 text-green-700',
  'Follow-up': 'bg-blue-100 text-blue-700',
  'No Sale': 'bg-red-100 text-red-700',
  'Transfer': 'bg-yellow-100 text-yellow-700',
};

const CallMonitoringDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>(MOCK_CALLS);
  const [recordings, setRecordings] = useState<Recording[]>(MOCK_RECORDINGS);
  const [selectedCall, setSelectedCall] = useState<ActiveCall | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isWhispering, setIsWhispering] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [activeTab, setActiveTab] = useState<'live' | 'recordings' | 'training'>('live');
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState<CallNote[]>([]);
  const [filterOutcome, setFilterOutcome] = useState('All');
  const [filterAgent, setFilterAgent] = useState('All');
  const [playingRec, setPlayingRec] = useState<string | null>(null);
  const [flagConfirm, setFlagConfirm] = useState<string | null>(null);

  // Tick durations on active calls
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCalls(prev => prev.map(c => {
        if (c.status !== 'active') return c;
        const s = c.durationSecs + 1;
        const mm = String(Math.floor(s / 60)).padStart(2, '0');
        const ss = String(s % 60).padStart(2, '0');
        return { ...c, durationSecs: s, duration: `${mm}:${ss}` };
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const listenTo = (call: ActiveCall) => {
    setSelectedCall(call);
    setIsListening(true);
    setIsWhispering(false);
    setIsMuted(false);
    setNotes('');
  };

  const stopListening = () => {
    setIsListening(false);
    setIsWhispering(false);
    setSelectedCall(null);
  };

  const randomCall = () => {
    const live = activeCalls.filter(c => c.status === 'active');
    if (live.length > 0) listenTo(live[Math.floor(Math.random() * live.length)]);
  };

  const flagCall = (id: string) => {
    setActiveCalls(prev => prev.map(c => c.id === id ? { ...c, flagged: !c.flagged } : c));
    setFlagConfirm(id);
    setTimeout(() => setFlagConfirm(null), 2000);
  };

  const saveNote = () => {
    if (!notes.trim() || !selectedCall) return;
    setSavedNotes(prev => [...prev, {
      callId: selectedCall.id,
      text: notes.trim(),
      timestamp: new Date().toLocaleTimeString()
    }]);
    setNotes('');
  };

  const toggleTraining = (id: string) => {
    setRecordings(prev => prev.map(r => r.id === id ? { ...r, isTraining: !r.isTraining } : r));
  };

  const filteredRecordings = recordings.filter(r =>
    (filterOutcome === 'All' || r.outcome === filterOutcome) &&
    (filterAgent === 'All' || r.agentName === filterAgent)
  );

  const trainingRecordings = recordings.filter(r => r.isTraining);

  const stats = {
    activeCalls: activeCalls.filter(c => c.status === 'active').length,
    totalToday: 47,
    avgDuration: '6:34',
    qualityScore: (recordings.reduce((s, r) => s + r.qualityScore, 0) / recordings.length).toFixed(1),
    soldToday: recordings.filter(r => r.outcome === 'Policy Sold').length,
  };

  const agentNames = ['All', ...Array.from(new Set(recordings.map(r => r.agentName)))];

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Call Monitoring Center</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Life &amp; Wealth Solutions — Real-Time Monitoring &amp; Quality Assurance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            {stats.activeCalls} Live Calls
          </span>
          <button
            onClick={randomCall}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            <SkipForward className="w-4 h-4" />
            Random Call
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Live Calls', value: stats.activeCalls, color: 'text-green-600', icon: <Phone className="w-5 h-5" /> },
          { label: 'Calls Today', value: stats.totalToday, color: 'text-blue-600', icon: <Clock className="w-5 h-5" /> },
          { label: 'Avg Duration', value: stats.avgDuration, color: 'text-purple-600', icon: <TrendingUp className="w-5 h-5" /> },
          { label: 'Quality Score', value: `${stats.qualityScore}/5.0`, color: 'text-yellow-600', icon: <Star className="w-5 h-5" /> },
          { label: 'Sold Today', value: stats.soldToday, color: 'text-emerald-600', icon: <CheckCircle className="w-5 h-5" /> },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </div>
              <span className={s.color}>{s.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {([['live', 'Live Calls'], ['recordings', 'Recordings'], ['training', 'Training Playlists']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setActiveTab(k)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === k ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
            {l}
            {k === 'training' && <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{trainingRecordings.length}</span>}
          </button>
        ))}
      </div>

      {/* LIVE CALLS TAB */}
      {activeTab === 'live' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Call List */}
          <div className="lg:col-span-2 space-y-3">
            {activeCalls.map(call => (
              <div key={call.id}
                className={`bg-white rounded-xl border-2 p-4 transition-all ${selectedCall?.id === call.id ? 'border-blue-500 bg-blue-50' : call.flagged ? 'border-red-300' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${call.agentType === 'AI' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {call.agentType === 'AI' ? `AI — ${call.agentName}` : `Human — ${call.agentName}`}
                      </span>
                      {call.policyType.includes('Whale') && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">🐋 WHALE</span>
                      )}
                      {call.status === 'hold' && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">ON HOLD</span>
                      )}
                      {call.flagged && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">⚑ Flagged</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{call.customerName}</p>
                    <p className="text-xs text-gray-500">{call.customerPhone} · {call.policyType}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {call.status === 'active' && (
                      <div className="flex items-center gap-1.5 text-green-600">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-sm font-mono font-bold">{call.duration}</span>
                      </div>
                    )}
                    <div className="flex gap-1.5">
                      <button onClick={() => listenTo(call)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
                        Listen In
                      </button>
                      <button onClick={() => flagCall(call.id)}
                        className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${call.flagged ? 'bg-red-100 text-red-600 border-red-200' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-red-50'}`}>
                        <Flag className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {flagConfirm === call.id && <span className="text-xs text-red-600">Flagged for review</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Monitoring Controls */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Monitoring Controls</h3>
              {isListening && selectedCall ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Now monitoring:</p>
                    <p className="text-sm font-bold text-gray-900">{selectedCall.agentName}</p>
                    <p className="text-xs text-gray-600">with {selectedCall.customerName}</p>
                    <p className="text-xs text-gray-500 mt-1">{selectedCall.policyType}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setIsMuted(!isMuted)}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${isMuted ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      {isMuted ? 'Muted' : 'Audio On'}
                    </button>
                    <button onClick={() => setIsWhispering(!isWhispering)}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${isWhispering ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {isWhispering ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                      Whisper
                    </button>
                  </div>
                  {isWhispering && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">Whisper mode active — only the agent hears you. Customer is unaware.</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Call Notes</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)}
                      placeholder="Type notes about this call..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <button onClick={saveNote}
                      className="mt-1.5 w-full px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200">
                      Save Note
                    </button>
                  </div>
                  {savedNotes.filter(n => n.callId === selectedCall.id).length > 0 && (
                    <div className="space-y-1.5">
                      {savedNotes.filter(n => n.callId === selectedCall.id).map((n, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-2">
                          <p className="text-xs text-gray-700">{n.text}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{n.timestamp}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={stopListening}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600">
                    <PhoneOff className="w-4 h-4" />
                    Stop Monitoring
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <PhoneOff className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No active session</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Listen In" on any call</p>
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button onClick={() => navigate('/ai-agents')}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                  <span>Manage AI Agents</span>
                  <Users className="w-4 h-4" />
                </button>
                <button onClick={() => setActiveTab('recordings')}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
                  <span>View Recordings</span>
                  <Play className="w-4 h-4" />
                </button>
                <button onClick={() => setActiveTab('training')}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
                  <span>Training Playlist</span>
                  <BookOpen className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RECORDINGS TAB */}
      {activeTab === 'recordings' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <select value={filterOutcome} onChange={e => setFilterOutcome(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
              <option value="All">All Outcomes</option>
              {['Policy Sold', 'Follow-up', 'No Sale', 'Transfer'].map(o => <option key={o}>{o}</option>)}
            </select>
            <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
              {agentNames.map(a => <option key={a}>{a}</option>)}
            </select>
            <span className="self-center text-sm text-gray-500 ml-auto">{filteredRecordings.length} recordings</span>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date', 'Agent', 'Customer', 'Policy', 'Duration', 'Quality', 'Outcome', 'Training', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRecordings.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.date}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{r.agentName}</p>
                        <span className={`text-xs ${r.agentType === 'AI' ? 'text-purple-600' : 'text-blue-600'}`}>{r.agentType}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.customerName}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{r.policyType}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{r.duration}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        <span className="font-semibold text-gray-800 text-xs">{r.qualityScore}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${outcomeColor[r.outcome]}`}>{r.outcome}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleTraining(r.id)}
                        className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${r.isTraining ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {r.isTraining ? '✓ Training' : '+ Add'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setPlayingRec(playingRec === r.id ? null : r.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs">
                        {playingRec === r.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        {playingRec === r.id ? 'Pause' : 'Play'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Playback bar */}
          {playingRec && (
            <div className="bg-gray-900 rounded-xl p-4 flex items-center gap-4">
              <button onClick={() => setPlayingRec(null)} className="text-gray-400 hover:text-white">
                <Pause className="w-5 h-5" />
              </button>
              <div className="flex-1">
                <div className="h-1.5 bg-gray-700 rounded-full">
                  <div className="h-1.5 bg-blue-500 rounded-full w-1/3 transition-all" />
                </div>
              </div>
              <span className="text-gray-400 text-xs font-mono">
                {recordings.find(r => r.id === playingRec)?.duration ?? ''}
              </span>
              <button onClick={() => setPlayingRec(null)} className="text-gray-500 hover:text-white text-xs">✕</button>
            </div>
          )}
        </div>
      )}

      {/* TRAINING PLAYLISTS TAB */}
      {activeTab === 'training' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-900">Training Playlist — {trainingRecordings.length} calls marked</p>
            <p className="text-xs text-blue-700 mt-0.5">These calls are flagged as examples for agent training. Use the Recordings tab to add or remove calls.</p>
          </div>
          {trainingRecordings.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No training recordings yet.</p>
              <p className="text-xs text-gray-400 mt-1">Go to Recordings and click "+ Add" to mark calls as training examples.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainingRecordings.map(r => (
                <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{r.agentName} → {r.customerName}</p>
                      <p className="text-xs text-gray-500">{r.policyType} · {r.date}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${outcomeColor[r.outcome]}`}>{r.outcome}</span>
                  </div>
                  <p className="text-xs text-gray-600 italic mb-3">"{r.notes}"</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      <span className="text-xs font-semibold text-gray-700">{r.qualityScore}/5.0</span>
                    </div>
                    <button onClick={() => setPlayingRec(playingRec === r.id ? null : r.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700">
                      {playingRec === r.id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      {playingRec === r.id ? 'Pause' : 'Play'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Twilio notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-800">Twilio Not Yet Connected</p>
            <p className="text-xs text-yellow-700 mt-0.5">
              Live call data, real audio monitoring, and whisper mode require Twilio to be configured.
              Go to <button onClick={() => navigate('/integrations')} className="underline font-medium">Integration Hub</button> to connect Twilio.
              All UI, recordings archive, training playlists, and controls are fully operational now.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallMonitoringDashboard;
